import { useTranslation } from "react-i18next";
import { Toggle } from "@/components/ui/Toggle";

export type ConfirmationOption = "photo" | "signature" | "both" | "disabled";

interface Props {
  /** Unique prefix for ids — two blocks can be mounted at once (BUG-504/508). */
  idPrefix: string;
  enabled: boolean;
  onEnabledChange: (next: boolean) => void;
  option: ConfirmationOption;
  onOptionChange: (next: ConfirmationOption) => void;
  /** Radius in metres; `undefined` means "no override". */
  radius: number | undefined;
  onRadiusChange: (next: number | undefined) => void;
  radiusError?: string;
}

/**
 * «Требовать подтверждение» — the switch + mode + radius trio.
 *
 * Replaces four verbatim copies (CreateOrder, OrderDetail, CreateTemplate,
 * EditTemplate) that shared identical semantics and drifted independently.
 *
 * The three-state contract is preserved by the HOST, not here: `null` means
 * "inherit the tenant setting", `false` means "explicitly off". This component
 * only reports whether the user wants an override at all (`enabled`) and, when
 * they do, which one — collapsing the two into a boolean here would lose the
 * inherit state (§10.2).
 */
export function ConfirmationBlock({
  idPrefix,
  enabled,
  onEnabledChange,
  option,
  onOptionChange,
  radius,
  onRadiusChange,
  radiusError,
}: Props) {
  const { t } = useTranslation();
  const switchId = `${idPrefix}-require-confirmation`;

  return (
    <div className="space-y-3 rounded-xl border border-gray-200 p-3">
      <div className="flex items-center justify-between gap-3">
        <label
          htmlFor={switchId}
          className="min-w-0 text-sm font-semibold text-gray-700"
        >
          {t("orders.form.requireConfirmation")}
        </label>
        <Toggle
          id={switchId}
          checked={enabled}
          onCheckedChange={onEnabledChange}
          aria-label={t("orders.form.requireConfirmation")}
        />
      </div>

      {enabled && (
        <div className="grid grid-cols-[minmax(0,1fr)_5.5rem] gap-2">
          {/*
            Owner, 2026-08-24: both labels were `sr-only` — present for a screen
            reader, invisible to everyone else. That left a bare number box marked
            only «м» beside the mode select, with nothing saying it is the
            confirmation RADIUS. Visible labels now; the ids and `aria-label` are
            unchanged, so the accessible names stay exactly as they were.
          */}
          <div className="min-w-0">
            <label
              htmlFor={`${idPrefix}-confirmation-mode`}
              className="mb-1 block text-xs font-medium text-gray-600"
            >
              {t("orders.confirmationOption")}
            </label>
            <select
              id={`${idPrefix}-confirmation-mode`}
              value={option}
              onChange={(e) =>
                onOptionChange(e.target.value as ConfirmationOption)
              }
              className="h-10 w-full rounded-xl border border-gray-200 px-3 text-sm text-gray-900 outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
            >
              <option value="photo">
                {t("orders.confirmationOptionPhoto")}
              </option>
              <option value="signature">
                {t("orders.confirmationOptionSignature")}
              </option>
              <option value="both">{t("orders.confirmationOptionBoth")}</option>
              <option value="disabled">
                {t("orders.confirmationOptionDisabled")}
              </option>
            </select>
          </div>
          <div className="min-w-0">
            <label
              htmlFor={`${idPrefix}-confirmation-radius`}
              className="mb-1 block truncate text-xs font-medium text-gray-600"
            >
              {t("orders.form.confirmationRadiusShort")}
            </label>
            <input
              id={`${idPrefix}-confirmation-radius`}
              type="number"
              min="0"
              step="1"
              value={radius ?? ""}
              onChange={(e) =>
                onRadiusChange(
                  e.target.value === "" ? undefined : Number(e.target.value),
                )
              }
              placeholder={t("orders.form.confirmationRadiusPlaceholder")}
              aria-label={t("orders.form.confirmationRadius")}
              className="h-10 w-full rounded-xl border border-gray-200 px-3 text-sm tabular-nums text-gray-900 outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
            />
          </div>
          {radiusError && (
            <p className="col-span-2 text-xs text-brand-danger">
              {radiusError}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
